import { useState } from 'react'
import { router } from '@inertiajs/react'
import api from '@/lib/axios'
import type { AdminPost } from '@/types/admin'

interface FormErrors {
  [key: string]: string
}

interface EditablePost extends AdminPost {
  tags: { id: number; name: string; slug: string }[]
  category: { id: number; name: string; slug: string } | null
}

export function usePostsEditVM(post: EditablePost) {
  const [title, setTitle] = useState(post.title)
  const [slug, setSlug] = useState(post.slug)
  const [excerpt, setExcerpt] = useState(post.excerpt ?? '')
  const [content, setContent] = useState(post.content ?? '')
  const [status, setStatus] = useState(post.status)
  const [visibility, setVisibility] = useState(post.visibility)
  const [categoryId, setCategoryId] = useState(String(post.category_id ?? ''))
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(post.tags.map((t) => t.id))
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(post.cover_image_url)
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [coverBlobKey, setCoverBlobKey] = useState<string | null>(null)
  const [metaTitle, setMetaTitle] = useState(post.meta_title ?? '')
  const [metaDescription, setMetaDescription] = useState(post.meta_description ?? '')
  const [publishedAt, setPublishedAt] = useState(
    post.published_at ? new Date(post.published_at).toISOString().slice(0, 16) : ''
  )
  const [sticky, setSticky] = useState(post.sticky)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false)

  const handleTitleChange = (val: string) => {
    setTitle(val)
  }

  const handleTagsChange = (ids: number[]) => {
    setSelectedTagIds(ids)
  }

  const handleCoverChange = (file: File | null) => {
    setCoverImage(file)
    setCoverUrl(null)
    setCoverBlobKey(null)
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => setCoverPreview(e.target?.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleMediaSelect = (url: string, blobKey: string) => {
    setCoverUrl(url)
    setCoverBlobKey(blobKey)
    setCoverPreview(url)
    setCoverImage(null)
  }

  const removeCover = () => {
    setCoverImage(null)
    setCoverPreview(null)
    setCoverUrl(null)
    setCoverBlobKey(null)
  }

  const handleCancel = () => {
    router.visit('/admin/posts')
  }

  const handleSubmit = async (submitStatus: string) => {
    setSaving(true)
    setErrors({})

    const formData = new FormData()
    formData.append('title', title)
    formData.append('slug', slug)
    formData.append('excerpt', excerpt)
    formData.append('content', content)
    formData.append('status', submitStatus)
    formData.append('visibility', visibility)
    if (categoryId) formData.append('category_id', categoryId)
    if (publishedAt) formData.append('published_at', publishedAt)
    if (metaTitle) formData.append('meta_title', metaTitle)
    if (metaDescription) formData.append('meta_description', metaDescription)
    formData.append('sticky', sticky ? 'true' : 'false')
    selectedTagIds.forEach((id) => formData.append('tag_ids[]', String(id)))
    if (coverImage) formData.append('cover_image', coverImage)
    if (coverBlobKey) formData.append('cover_image_blob_key', coverBlobKey)
    formData.append('_method', 'PATCH')

    try {
      const { data } = await api.post(`/admin/posts/${post.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (data.success) {
        router.visit('/admin/posts')
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { errors?: string[] } } }).response
        if (response?.data?.errors) {
          setErrors({ base: response.data.errors.join(', ') })
        }
      }
    } finally {
      setSaving(false)
    }
  }

  return {
    title,
    slug,
    setSlug,
    excerpt,
    setExcerpt,
    content,
    setContent,
    status,
    setStatus: (v: string) => setStatus(v as typeof status),
    visibility,
    setVisibility: (v: string) => setVisibility(v as typeof visibility),
    categoryId,
    setCategoryId,
    selectedTagIds,
    handleTagsChange,
    coverPreview,
    metaTitle,
    setMetaTitle,
    metaDescription,
    setMetaDescription,
    publishedAt,
    setPublishedAt,
    sticky,
    setSticky,
    saving,
    errors,
    mediaDialogOpen,
    setMediaDialogOpen,
    handleTitleChange,
    handleCoverChange,
    handleMediaSelect,
    removeCover,
    handleCancel,
    handleSubmit,
  }
}
